import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowRight, Eye, EyeOff, Check } from 'lucide-react';
import { useBrand } from '../../context/BrandContext';

const AbleRegister = () => {
  const { brand, brandConfig } = useBrand();
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { register, loading } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    if (!acceptTerms) {
      setError('You must accept the terms and conditions to register');
      return;
    }

    try {
      await register(email, username, password, fullName, organizationName, acceptTerms, brand);
      navigate('/');
    } catch (err) {
      setError(err.message);
    }
  };

  const getPasswordStrength = () => {
    if (password.length < 6) return 0;
    if (password.length < 8) return 1;
    if (password.match(/(?=.*[a-z])(?=.*[A-Z])/)) return 2;
    if (password.match(/(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])/)) return 3;
    return 3;
  };

  useEffect(() => {
    setFullName(null);
    setOrganizationName(null);
  }, []);

  const passwordStrength = getPasswordStrength();

  const strengthColors = ['bg-red-400', 'bg-orange-400', 'bg-yellow-400', 'bg-[#2ECC71]'];
  const strengthLabels = ['Weak', 'Fair', 'Good', 'Strong'];

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Hero Dark */}
      <div className="hidden md:flex md:w-[42%] bg-[#0B1120] flex-col justify-between p-12 relative overflow-hidden">
        {/* Subtle lime accent */}
        <div className="absolute -bottom-16 -left-16 w-64 h-64 bg-[#C6F135] rounded-full opacity-[0.07] blur-3xl" />
        <div className="absolute top-1/3 -right-20 w-48 h-48 bg-[#C6F135] rounded-full opacity-[0.05] blur-2xl" />

        <div className="relative z-10">
          <img src="/able_logo_white.png" alt="Able" className="h-8 mb-16" />
          <h1 className="font-['Satoshi'] text-4xl font-bold text-white tracking-tight leading-tight">
            Get started<br />with Able
          </h1>
          <p className="mt-4 text-[#A1A1AA] text-lg leading-relaxed max-w-sm">
            Build forms, pages, and manage your customers — all in one platform.
          </p>
        </div>

        {/* Decorative dots */}
        <div className="relative z-10 flex space-x-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-[#C6F135]" />
          <div className="w-1.5 h-1.5 rounded-full bg-[#C6F135] opacity-60" />
          <div className="w-1.5 h-1.5 rounded-full bg-[#C6F135] opacity-30" />
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-white">
        <div className="w-full max-w-[400px]">
          {/* Mobile logo */}
          <div className="md:hidden mb-10">
            <img src="/able-logo.svg" alt="Able" className="h-7 mb-6" />
          </div>

          <h2 className="font-['Satoshi'] text-2xl font-bold text-gray-900 tracking-tight">
            Create your account
          </h2>
          <p className="mt-2 text-gray-500 text-sm">
            Start building in minutes. No credit card required.
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="block w-full px-4 py-3 border border-[#D4D4D8] rounded-[0.75rem] text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#2ECC71] focus:ring-1 focus:ring-[#2ECC71] transition-colors"
                placeholder="you@example.com"
              />
            </div>

            {/* Name */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1.5">
                Name
              </label>
              <input
                id="username"
                name="username"
                type="text"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="block w-full px-4 py-3 border border-[#D4D4D8] rounded-[0.75rem] text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#2ECC71] focus:ring-1 focus:ring-[#2ECC71] transition-colors"
                placeholder="Your name"
              />
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full px-4 py-3 pr-11 border border-[#D4D4D8] rounded-[0.75rem] text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#2ECC71] focus:ring-1 focus:ring-[#2ECC71] transition-colors"
                  placeholder="Create a password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3.5 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>

              {/* Password Strength */}
              {password && (
                <div className="mt-2.5">
                  <div className="flex space-x-1 mb-1.5">
                    {[0, 1, 2, 3].map((level) => (
                      <div
                        key={level}
                        className={`h-1 flex-1 rounded-full transition-colors ${
                          level <= passwordStrength
                            ? strengthColors[passwordStrength]
                            : 'bg-gray-200'
                        }`}
                      />
                    ))}
                  </div>
                  <div className="text-xs flex items-center">
                    {password.length >= 6 ? (
                      <>
                        <Check className="h-3 w-3 text-[#2ECC71] mr-1" />
                        <span className="text-gray-500">{strengthLabels[passwordStrength]} password</span>
                      </>
                    ) : (
                      <span className="text-gray-400">At least 6 characters</span>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-[0.75rem] px-4 py-3">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            {/* Terms */}
            <div>
              <label className="flex items-start cursor-pointer group">
                <div className="relative flex-shrink-0 mt-0.5">
                  <input
                    type="checkbox"
                    required
                    checked={acceptTerms}
                    onChange={(e) => setAcceptTerms(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-5 h-5 border-2 border-[#D4D4D8] rounded-md bg-white transition-all duration-200 group-hover:border-[#2ECC71] peer-checked:bg-[#2ECC71] peer-checked:border-[#2ECC71] peer-focus:ring-2 peer-focus:ring-[#2ECC71]/20 flex items-center justify-center" />
                  <Check className="w-3 h-3 text-white absolute inset-0 m-auto opacity-0 peer-checked:opacity-100 transition-opacity duration-200 pointer-events-none" strokeWidth={3} />
                </div>
                <span className="ml-3 text-sm text-gray-600">
                  I agree to the{' '}
                  <a
                    href={brandConfig.termsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#2ECC71] hover:text-[#27AE60] font-medium underline transition-colors"
                  >
                    terms and conditions
                  </a>
                </span>
              </label>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="group w-full flex justify-center items-center py-3 px-4 text-sm font-semibold rounded-[0.75rem] text-white bg-[#2ECC71] hover:bg-[#27AE60] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2ECC71] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Creating account...
                </div>
              ) : (
                <div className="flex items-center">
                  Create account
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                </div>
              )}
            </button>
          </form>

          {/* Sign In Link */}
          <p className="mt-8 text-center text-sm text-gray-500">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-[#2ECC71] hover:text-[#27AE60] transition-colors">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AbleRegister;
